/**
 * `windowLane` — where a COMBAT.app window **first opens**: in the free lane between the board's
 * left and right columns, never on top of the unit column (`decisions/0191`, call (a)).
 *
 * The left column is the unit column (`UnitColumn.tsx`: the unit panel and the docked command menu
 * above it) when it holds anything (the TURN rail that also sat there is retired, `decisions/0190`). The right column is the tool rail plus its open options strip or
 * palette, and the Map key when it shows. The lane is what is left of the stage between them, with
 * {@link LANE_GUTTER} each side. It is measured at open time, not assumed: the menu comes and goes.
 *
 * A {@link CombatWindowSpec}'s `defaultPos.x` is the window's offset into the lane (the cascade
 * stagger for inspectors, a fixed spread for the singletons), and `defaultPos.y` stays absolute.
 * {@link firstOpenGeometry} turns that into the absolute pixel spec `openWindow` seeds from, and
 * lowers `y` when chrome hanging over the stage (the round header, `decisions/0190`) reaches below it. Only a
 * first open reads it; a reopen and persisted stable-id geometry keep their stored place
 * (`decisions/0090`).
 */
import { PAD_LEFT, PAD_TOP } from "../../routes/home/windowGeometry";
import type { CombatWindowSpec } from "./windows";

/** The space kept between the lane and each column. */
export const LANE_GUTTER = 16;
/** The narrowest a window opens. Below it the window keeps this width and overhangs the right column, never the left. */
export const LANE_MIN_W = 360;

/** A horizontal span in the windows' container coordinates (card edges, not the padded outer box). */
export type Span = { left: number; right: number };
/** The free lane: a horizontal span plus the lowest top edge a card may open at. */
export type Lane = Span & { top: number };

/**
 * The free lane: from the right edge of the rightmost left-column member to the left edge of the
 * leftmost right-column member, inside the stage, with a gutter on each side. Its top is the stage's
 * top or a gutter below chrome hanging over the stage (`above`, e.g. a round header), whichever is lower.
 */
export function windowLane(args: { stage: Span & { top: number }; leftColumn: Span[]; rightColumn: Span[]; above?: { bottom: number }[] }): Lane {
  const { stage, leftColumn, rightColumn, above = [] } = args;
  return {
    left: Math.max(stage.left, ...leftColumn.map((s) => s.right)) + LANE_GUTTER,
    right: Math.min(stage.right, ...rightColumn.map((s) => s.left)) - LANE_GUTTER,
    top: Math.max(stage.top, ...above.map((a) => a.bottom + LANE_GUTTER)),
  };
}

/**
 * A window's first-open spec, placed in the lane. The card opens at the lane's left edge plus the
 * spec's offset, shifted left as far as the lane's left edge to fit. A card wider than the lane
 * opens at the lane's width, down to {@link LANE_MIN_W}. When the card fills the lane the x stagger
 * collapses to the lane's left edge; the y stagger still separates the cascade.
 */
export function firstOpenGeometry(spec: CombatWindowSpec, lane: Lane): CombatWindowSpec {
  const w = Math.max(LANE_MIN_W, Math.min(spec.defaultSize.w, lane.right - lane.left));
  const cardLeft = Math.max(lane.left, Math.min(lane.left + spec.defaultPos.x, lane.right - w));
  return {
    ...spec,
    defaultPos: { x: cardLeft - PAD_LEFT, y: Math.max(spec.defaultPos.y, lane.top - PAD_TOP) },
    defaultSize: { ...spec.defaultSize, w },
  };
}

const boxOf = (el: Element, origin: { left: number; top: number }) => {
  const r = el.getBoundingClientRect();
  return { left: r.left - origin.left, right: r.right - origin.left, top: r.top - origin.top, bottom: r.bottom - origin.top };
};

/**
 * Measure the lane off the live DOM, in the coordinates of `container` (the windows' containing
 * block, `.combat-app`). Returns `undefined` when the stage is not mounted.
 *
 * The unit column is read from its layout box, not its painted rect: its menu can be mid-enter (a
 * transform), and it still owns its column either way. An empty
 * column (nothing selected or hovered) is zero-wide and owns nothing.
 */
export function readWindowLane(container: Element | null): Lane | undefined {
  const stageEl = container?.querySelector(".combat-stage-wrap");
  if (!container || !stageEl) return undefined;
  const origin = container.getBoundingClientRect();
  const stage = boxOf(stageEl, origin);
  const leftColumn: Span[] = [];
  const column = stageEl.querySelector<HTMLElement>(".unit-column");
  if (column && column.offsetWidth > 0) leftColumn.push({ left: stage.left + column.offsetLeft, right: stage.left + column.offsetLeft + column.offsetWidth });
  // The right column: the tool rail, an open options strip (`BoardToolRail.tsx`) and the Map key.
  const rightColumn = Array.from(stageEl.querySelectorAll(".combat-tool-rail, .combat-tool-strip, .combat-map-key"), (el) => boxOf(el, origin));
  const roundHeader = container.querySelector(".combat-roundheader");
  return windowLane({ stage, leftColumn, rightColumn, above: roundHeader ? [boxOf(roundHeader, origin)] : [] });
}
